import i18n from 'helpers/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import messages from './messages';
import useCurrentUser from 'hooks/useCurrentUser';
import { toastError } from 'components/Toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterSidebar, { FilterState } from '../FilterSideBar/FilterSideBar';
import { SECTION, searchParamsToState, stateToSearchParams } from './urlState';
import { LrAction } from 'components/LearningResources/ResourcesSection/useLearningResources';
import { Loader, Popup } from 'semantic-ui-react';
import { Formik } from 'components/EnhancedForm';
import SortDropdown from 'components/LearningResources/SortDropdown';
import ResourcesSection from 'components/LearningResources/ResourcesSection';
import RecommendationBanner from 'components/LearningResources/RecommendationBanner/RecommendationBanner';
import { RecommendationMetadata } from 'types/api/ClassScoresRecommendation';
import DownArrow from 'images/arrow_drop_down.svg';
import QuestionHelp from 'images/questionHelp.svg';
import RouteHelpers from 'helpers/routes';
import { hasLearningResourcesSubscription } from 'components/LearningResources/utils';
import { Filter } from '@teachstonellc/component-library/dist/icons';

const SECTION_LABELS: Record<string, { title: string; description: string }> = {
  [SECTION.ALL_RESOURCES]: {
    title: 'All Resources',
    description: 'View all of our resources'
  },
  [SECTION.RECOMMENDATIONS]: {
    title: 'My Recommendations',
    description: 'Resources assigned for my professional development.'
  },
  [SECTION.STARTED]: {
    title: 'Getting Started',
    description: 'Essential resources to help me begin my learning journey.'
  },
  [SECTION.IMPORTANT]: {
    title: 'Important Resources',
    description: 'A collection of all resources I have marked as important.'
  },
  [SECTION.CLASSROOM]: {
    title: 'Using CLASS Scores',
    description:
      'Resource suggestions based on my most recent CLASS observation data.'
  },
  [SECTION.HISTORY]: {
    title: 'Personalized Picks',
    description: 'Recommendations based on what I’ve recently explored.'
  },
  [SECTION.COMPLETED]: {
    title: 'My Completed Resources',
    description: 'Archive of resources I have finished.'
  }
};

const DEFAULT_FILTERS: FilterState = {
  query: '',
  class_version: [],
  age_level: [],
  dimension: [],
  language: '',
  resource_type: []
};

const PREFERRED_LANGUAGE_MAP: Record<string, string> = {
  en: 'english',
  es: 'spanish'
};

function getDefaultFilters(preferredLanguage: string): FilterState {
  return {
    ...DEFAULT_FILTERS,
    language: PREFERRED_LANGUAGE_MAP[preferredLanguage] ?? ''
  };
}

function LearningResourcesHomePage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultLanguage = PREFERRED_LANGUAGE_MAP[user.preferred_language] ?? '';

  const {
    filters,
    sortBy,
    sortDir,
    page,
    section: sectionSelected
  } = useMemo(
    () => searchParamsToState(searchParams, { defaultLanguage }),
    [searchParams, defaultLanguage]
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [recommendation, setRecommendation] =
    useState<RecommendationMetadata | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const commitState = useCallback(
    (next: {
      filters?: FilterState;
      sortBy?: string;
      page?: number;
      section?: string;
      resetPage?: boolean;
    }) => {
      setSearchParams(
        stateToSearchParams({
          filters: next.filters ?? filters,
          sortBy: next.sortBy ?? sortBy,
          section: next.section ?? sectionSelected,
          page: next.resetPage ? 1 : (next.page ?? page)
        })
      );
    },
    [filters, sortBy, sectionSelected, page, setSearchParams]
  );

  const isCoachOnly = useCallback(() => {
    return (
      user.roles.includes('lr_coach') && !user.roles.includes('lr_educator')
    );
  }, [user.roles]);

  // Check subscription
  useEffect(() => {
    if (!hasLearningResourcesSubscription(user)) {
      toastError({
        description: i18n.ft(messages.noSubscription)
      });
      navigate(RouteHelpers.getPath('app'));
    }
  }, [user, navigate]);

  useEffect(() => {
    setSettingsLoading(false);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (isMenuOpen && !target.closest('.section-dropdown')) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  function handleSort(value: string) {
    commitState({ sortBy: value, resetPage: true });
  }

  function handleFilterChange(newFilters: FilterState) {
    commitState({ filters: newFilters, resetPage: true });
  }

  function handleSectionChange(key: string) {
    setIsMenuOpen(false);
    commitState({
      section: key,
      filters: getDefaultFilters(user.preferred_language),
      resetPage: true
    });
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      commitState({ page: newPage });
    },
    [commitState]
  );

  function getSectionAction(): string {
    switch (sectionSelected) {
      case SECTION.ALL_RESOURCES:
        return 'allResources';
      case SECTION.RECOMMENDATIONS:
        return 'recommended';
      case SECTION.STARTED:
        return 'gettingStarted';
      case SECTION.IMPORTANT:
        return 'important';
      case SECTION.HISTORY:
        return 'viewingHistory';
      case SECTION.CLASSROOM:
        return 'classroomScores';
      case SECTION.COMPLETED:
        return 'completedResources';
      default:
        return 'allResources';
    }
  }

  function getSectionToolTip() {
    switch (sectionSelected) {
      case SECTION.ALL_RESOURCES:
        return i18n.ft(messages.allResources);
      case SECTION.RECOMMENDATIONS:
        return i18n.ft(messages.recommendations);
      case SECTION.STARTED:
        return i18n.ft(messages.started);
      case SECTION.IMPORTANT:
        return i18n.ft(messages.important);
      case SECTION.CLASSROOM:
        return i18n.ft(messages.classroom);
      case SECTION.HISTORY:
        return i18n.ft(messages.history);
      case SECTION.COMPLETED:
        return i18n.ft(messages.completed);
      default:
        return '';
    }
  }
  function renderSectionDropdown() {
    const sectionTitle =
      SECTION_LABELS[sectionSelected]?.title || 'All Resources';

    return (
      <div className="flex items-center mb-4">
        <div className="section-dropdown relative inline-block text-left">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="inline-flex pr-4 py-2 hover:bg-gray-50 focus:outline-none"
          >
            <h2 className="text-black text-xl font-semibold">{sectionTitle}</h2>
            <span className="ml-2 self-center">
              <img src={DownArrow} alt="" />
            </span>
          </button>

          {isMenuOpen && (
            <div className="min-w-[341px] absolute mt-2 rounded-md shadow-lg bg-white border-[#0A9CCA] border-2 ring-1 ring-black ring-opacity-5 z-30">
              <ul className="py-1" role="menu" aria-orientation="vertical">
                {Object.entries(SECTION_LABELS).map(([key, label]) => {
                  if (key === SECTION.ALL_RESOURCES && isCoachOnly()) {
                    return null;
                  }
                  return (
                    <li
                      key={key}
                      className={`px-4 py-2 text-base text-gray-700 hover:bg-[#E6F5FA] hover:text-gray-900 cursor-pointer ${
                        sectionSelected === key ? 'font-bold' : ''
                      }`}
                      onClick={() => handleSectionChange(key)}
                    >
                      {label.title}
                      <div className="font-normal text-xs">
                        {label.description}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <span className="ml-2 self-center inline-block">
          <Popup
            content={
              <span>
                <p className="text-sm">{getSectionToolTip()}</p>
              </span>
            }
            trigger={<img src={QuestionHelp} alt="" />}
            position="bottom left"
            flowing
          />
        </span>

        {!isCoachOnly() && (
          <span
            className="block lg:hidden ml-2 content-center cursor-pointer"
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          >
            <Filter size={24} color="#00729B" />
          </span>
        )}
      </div>
    );
  }

  function renderUnifiedView() {
    return (
      <div className="flex gap-6">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block flex-shrink-0 sticky top-2 self-start max-w-xs">
          {!isCoachOnly() && renderSectionDropdown()}
          {sectionSelected === SECTION.CLASSROOM ? (
            <RecommendationBanner metadata={recommendation} />
          ) : (
            <FilterSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Desktop sort bar */}
          {sectionSelected !== SECTION.CLASSROOM && (
            <div className="hidden lg:flex justify-end mb-2">
              <div className="z-20 [&_.ui.dropdown]:!text-sm [&_.item]:!text-sm">
                <Formik
                  enableReinitialize
                  initialValues={{ sort: sortBy }}
                  onSubmit={() => {}}
                >
                  <SortDropdown handleSort={handleSort} />
                </Formik>
              </div>
            </div>
          )}

          <ResourcesSection
            action={getSectionAction() as LrAction}
            filters={filters}
            sortBy={sortBy}
            sortDir={sortDir}
            educatorId={
              sectionSelected === SECTION.CLASSROOM ||
              sectionSelected === SECTION.COMPLETED
                ? user.id
                : undefined
            }
            onRecommendation={setRecommendation}
            page={page}
            onPageChange={handlePageChange}
            showTitle={false}
          />
        </div>
      </div>
    );
  }

  if (settingsLoading) {
    return <Loader active />;
  }

  return (
    <>
      <div className="mb-16 px-4 md:px-0 md:py-8">
        <div className="mb-16 px-4 md:px-0" style={{ overflowAnchor: 'auto' }}>
          <div className="block lg:hidden">
            {renderSectionDropdown()}
            {sectionSelected === SECTION.CLASSROOM ? (
              <RecommendationBanner metadata={recommendation} />
            ) : (
              mobileFiltersOpen && (
                <FilterSidebar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
              )
            )}
          </div>
          {renderUnifiedView()}
        </div>
      </div>
    </>
  );
}

export default LearningResourcesHomePage;
